"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@immi/ui/lib/utils";
import { Button } from "@immi/ui/components/button";

type PaginationProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  siblings?: number;
  className?: string;
  label?: string;
};

const ELLIPSIS = "ellipsis" as const;

type PageSlot = number | typeof ELLIPSIS;

function buildPageSlots(
  page: number,
  totalPages: number,
  siblings: number,
): PageSlot[] {
  if (totalPages <= 1) return [1];

  const first = 1;
  const last = totalPages;
  const start = Math.max(first + 1, page - siblings);
  const end = Math.min(last - 1, page + siblings);

  const slots: PageSlot[] = [first];
  if (start > first + 1) slots.push(ELLIPSIS);
  for (let current = start; current <= end; current += 1) slots.push(current);
  if (end < last - 1) slots.push(ELLIPSIS);
  if (last > first) slots.push(last);

  return slots;
}

function Pagination({
  page,
  totalPages,
  onPageChange,
  siblings = 2,
  className,
  label = "Paginação",
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const slots = buildPageSlots(page, totalPages, siblings);

  return (
    <nav
      aria-label={label}
      data-slot="pagination"
      className={cn("flex items-center justify-center gap-1", className)}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Página anterior"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft />
      </Button>
      {slots.map((slot, index) =>
        slot === ELLIPSIS ? (
          <span
            key={`ellipsis-${index}`}
            aria-hidden
            className="px-1 text-sm text-ink-subtle"
          >
            …
          </span>
        ) : (
          <Button
            key={slot}
            variant={slot === page ? "default" : "ghost"}
            size="icon-sm"
            aria-label={`Página ${slot}`}
            aria-current={slot === page ? "page" : undefined}
            onClick={() => onPageChange(slot)}
          >
            {slot}
          </Button>
        ),
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Próxima página"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight />
      </Button>
    </nav>
  );
}

export { Pagination, buildPageSlots };
export type { PaginationProps };
